import React, { useState } from 'react';
import { adminApi } from '../../config/axios';
import { useGlobal } from '../../contexts/GlobalContext';
import Modal from './Modal';

const DEFAULT_MESSAGE = "سنعود قريباً / We'll be back shortly";

/**
 * Emergency Kill Switch trigger for the admin panel.
 *
 * - companyId: required
 * - companyName: shown in confirmation
 * - active: current emergency_active state; controls button color + verb
 * - variant: 'card' (compact icon) | 'lg' (full button with label)
 * - onChanged: optional callback after activate/deactivate completes
 */
export default function EmergencyControl({
  companyId,
  companyName,
  active = false,
  variant = 'card',
  onChanged,
}) {
  const { showToast } = useGlobal();
  const [open, setOpen]       = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [busy, setBusy]       = useState(false);

  const activate = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/company/${companyId}/emergency/activate`, {
        message: message?.trim() || DEFAULT_MESSAGE,
      });
      showToast('success', 'Emergency Stop', `All ${companyName ?? 'company'} screens switched to safe content.`);
      setOpen(false);
      onChanged?.(true);
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not activate emergency mode.');
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/company/${companyId}/emergency/deactivate`);
      showToast('success', 'Emergency Cleared', 'Devices resuming normal playback.');
      onChanged?.(false);
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not deactivate.');
    } finally {
      setBusy(false);
    }
  };

  const tooltip = active
    ? 'Emergency active — click to resume playback'
    : 'Emergency Stop · إيقاف الطوارئ (all company screens)';

  if (variant === 'card') {
    return (
      <>
        <button
          type="button"
          onClick={active ? deactivate : () => setOpen(true)}
          title={tooltip}
          aria-label={tooltip}
          disabled={busy}
          className={`flex-1 px-2 py-1.5 text-xs rounded-lg border transition-colors inline-flex items-center justify-center gap-1 ${
            active
              ? 'bg-danger/15 text-danger border-danger/30 animate-pulse hover:bg-danger/25'
              : 'bg-white/[0.04] text-text-muted border-white/[0.06] hover:bg-danger/10 hover:text-danger'
          }`}
        >
          <span aria-hidden="true">🚨</span>
          <span className="hidden sm:inline">{active ? 'Active' : 'Emergency'}</span>
        </button>
        <ConfirmModal
          open={open}
          companyName={companyName}
          message={message}
          setMessage={setMessage}
          onClose={() => setOpen(false)}
          onConfirm={activate}
          busy={busy}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={active ? deactivate : () => setOpen(true)}
        disabled={busy}
        className={
          active
            ? 'btn-secondary border-danger/40 text-danger animate-pulse'
            : 'btn-secondary border-danger/30 text-danger hover:bg-danger/10'
        }
      >
        <span className="mr-2" aria-hidden="true">🚨</span>
        {active ? 'Resume Normal Operation' : 'Emergency Stop All Screens'}
      </button>
      <ConfirmModal
        open={open}
        companyName={companyName}
        message={message}
        setMessage={setMessage}
        onClose={() => setOpen(false)}
        onConfirm={activate}
        busy={busy}
      />
    </>
  );
}

function ConfirmModal({ open, companyName, message, setMessage, onClose, onConfirm, busy }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="🚨 Activate Emergency Stop"
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary bg-danger hover:bg-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Activating…' : '🚨 Activate'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-text-muted leading-relaxed">
          This will <strong className="text-text-main">immediately</strong> switch every screen at{' '}
          <strong className="text-text-main">{companyName ?? 'this company'}</strong> to a branded
          safety screen showing their logo and the message below.
        </p>
        <label className="block text-xs font-semibold uppercase text-text-muted">
          Message shown on screens
        </label>
        <textarea
          className="input-base h-20 resize-none"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={DEFAULT_MESSAGE}
        />
      </div>
    </Modal>
  );
}
